import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useQueryParameter } from '@/context/QueryParameter';
import {
  InvoiceReportColumn,
  InvoiceReportsSearchQuery,
  InvoiceReportTarget,
  mapApiSortFieldToColumnKey,
  mapColumnKeyToApiSortField,
  MultiColumnSortState,
  SortField,
  SortOrder,
} from '@/features/invoice_reports/types';
import { useUpdateURLSearchParams } from '@/hooks/useConvertToURLSearchParams';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useTranslation } from '@/i18n';
import { isEqual } from '@/utils/isEqual';
import {
  InvoiceReportOrderByField,
  SearchedInvoiceReport,
  SearchInvoiceReportsQueryMfFile,
  SearchInvoiceReportsQueryPaymentStatus,
  SearchInvoiceReportsQueryStatus,
} from 'ap-openapi';
import { Dayjs } from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  convertFormDataToApiQuery,
  InvoiceReportsSearchFormData,
} from '../../Search/hooks/useSearchForm';

const isValidStatus = (
  value: string
): value is SearchInvoiceReportsQueryStatus =>
  Object.values<string>(SearchInvoiceReportsQueryStatus).includes(value);

const isValidPaymentStatus = (
  value: string
): value is SearchInvoiceReportsQueryPaymentStatus =>
  Object.values<string>(SearchInvoiceReportsQueryPaymentStatus).includes(value);

export const useEmptyState = (isSearchEmpty: boolean) => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (isSearchEmpty) {
      return {
        emptyType: 'search' as const,
        emptyMessage: t('invoice_reports.table.no_search_results'),
      };
    }

    return {
      emptyType: 'object' as const,
      emptyMessage: t('invoice_reports.table.empty_message'),
    };
  }, [isSearchEmpty, t]);
};

export const useResponseToInvoiceReportColumn = <T extends InvoiceReportColumn>(
  item: SearchedInvoiceReport
): T => {
  const result = {
    id: item.id,
    number: item.number,
    created_at: item.created_at,
    submitted_at: item.submitted_at,
    office_member: {
      name: item.office_member?.name || '',
      number: item.office_member?.number || '',
    },
    title: item.title,
    status: item.status || {},
    ap_payee_name: item.ap_payee_name,
    book_date: item.book_date,
    due_date: item.due_date,
    total_amount: item.total_amount,
    foreign_currency_amount: item.foreign_currency_amount,
    report_approval_names: item.report_approval_names,
    approved_at: item.approved_at,
    payment_date: item.payment_date,
    payment_status: item.payment_status,
    invoice_file: item.invoice_file
      ? {
          name: item.invoice_file.filename || '',
          receipt_type: item.invoice_file.receipt_type || '',
        }
      : undefined,
    automatic_status_waiting: item.automatic_status_waiting,
    timestamp_status_creating: item.timestamp_status_creating,
    error_messages: item.error_messages,
    is_waiting: item.is_waiting,
    detail_path: item.detail_path,
    share_path: item.share_path,
    edit_path: item.edit_path,
    copy_path: item.copy_path,
    is_copyable: item.is_copyable,
    edit_action: item.edit_action ?? {
      visible: false,
      allowed: false,
      reason: null,
    },
    delete_action: item.delete_action ?? {
      visible: false,
      allowed: false,
      reason: null,
    },
    cancel_action: item.cancel_action ?? {
      visible: false,
      allowed: false,
      reason: null,
    },
    share_action: item.share_action,
    viewers_count: item.viewers_count,
  };
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return result as unknown as T;
};

// Empty array constant for stable references
const EMPTY_SELECTED_DATA: InvoiceReportTarget[] = [];

export const useTableSelection = (data: readonly InvoiceReportColumn[]) => {
  const [selectedRowData, setSelectedRowData] =
    useState<InvoiceReportTarget[]>(EMPTY_SELECTED_DATA);

  // Use ref to store current data for use in callbacks without causing re-renders
  const dataRef = useRef(data);
  dataRef.current = data;

  const selectedRowKeys = useMemo(
    () => selectedRowData.map((rowData) => rowData.report_id),
    [selectedRowData]
  );

  const selectableItems = useMemo(
    () => data.filter((item) => item.delete_action.allowed),
    [data]
  );

  // Use functional setState for stable callback references
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const currentData = dataRef.current;
      const allKeys = currentData
        .filter((item) => item.delete_action.allowed)
        .map((item) => ({
          report_id: item.id,
          number: item.number,
        }));
      setSelectedRowData(allKeys);
    } else {
      setSelectedRowData(EMPTY_SELECTED_DATA);
    }
  }, []);

  const handleSelectRow = useCallback(
    (record: InvoiceReportColumn, checked: boolean) => {
      setSelectedRowData((prev) => {
        if (checked) {
          return [
            ...prev,
            {
              report_id: record.id,
              number: record.number,
            },
          ];
        }
        return prev.filter((rowData) => rowData.report_id !== record.id);
      });
    },
    []
  );

  // Use primitive length values for derived state
  const selectableCount = selectableItems.length;
  const selectedCount = selectedRowData.length;

  const isAllSelected =
    selectableCount > 0 && selectedCount === selectableCount;
  const isAllDisabled = selectableCount === 0;

  return {
    selectedRowData,
    selectedRowKeys,
    handleSelectAll,
    handleSelectRow,
    isAllSelected,
    isAllDisabled,
  };
};

const formatDateRange = (
  from: string | Dayjs,
  to: string | Dayjs
): (string | Dayjs)[] | undefined => {
  if (!from || !to) {
    return ['', ''];
  }
  return [from, to];
};

export const useInvoiceReportsFormInitial = (
  canAgentEntry: boolean
): InvoiceReportsSearchFormData => {
  const location = useLocation();
  const { getDateParam, getArrayParam } = useQueryParams();
  const { currentOfficeMember } = useLoginInfo();

  return useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = Number(searchParams.get('page')) || 1;
    const sortFields = searchParams.get('sort_fields');
    const sortOrders = searchParams.get('sort_orders');

    const statusValue = searchParams.get('status') || undefined;
    const mfFileValue = searchParams.get('mf_file') || undefined;
    const paymentStatusValue = searchParams.get('payment_status') || undefined;

    return {
      number: searchParams.get('number') || '',
      query: searchParams.get('query') || '',
      status:
        statusValue && isValidStatus(statusValue) ? statusValue : undefined,
      payment_status:
        paymentStatusValue && isValidPaymentStatus(paymentStatusValue)
          ? paymentStatusValue
          : undefined,
      ap_payee_ids: getArrayParam(searchParams, 'ap_payee_ids') ?? [],
      created_date: formatDateRange(
        getDateParam(searchParams, 'created_at_from'),
        getDateParam(searchParams, 'created_at_to')
      ),
      submitted_date: formatDateRange(
        getDateParam(searchParams, 'submitted_at_from'),
        getDateParam(searchParams, 'submitted_at_to')
      ),
      due_date: formatDateRange(
        getDateParam(searchParams, 'due_date_from'),
        getDateParam(searchParams, 'due_date_to')
      ),
      approved_date: formatDateRange(
        getDateParam(searchParams, 'approved_at_from'),
        getDateParam(searchParams, 'approved_at_to')
      ),
      book_date: formatDateRange(
        getDateParam(searchParams, 'book_date_from'),
        getDateParam(searchParams, 'book_date_to')
      ),
      payment_date: formatDateRange(
        getDateParam(searchParams, 'payment_date_from'),
        getDateParam(searchParams, 'payment_date_to')
      ),
      agent_applicant_office_member_ids:
        getArrayParam(searchParams, 'agent_applicant_office_member_ids') ||
        (canAgentEntry && currentOfficeMember?.id
          ? [currentOfficeMember.id]
          : []),
      viewer_ids: getArrayParam(searchParams, 'viewer_office_member_ids') ?? [],
      total_amount_from: searchParams.get('total_amount_from') || '',
      total_amount_to: searchParams.get('total_amount_to') || '',
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      mf_file: mfFileValue as SearchInvoiceReportsQueryMfFile | '',
      page,
      sort_fields: sortFields || undefined,
      sort_orders: sortOrders || undefined,
    };
  }, [
    getDateParam,
    getArrayParam,
    location.search,
    currentOfficeMember?.id,
    canAgentEntry,
  ]);
};

// Empty sort orders array constant for stable reference
const EMPTY_SORT_ORDERS: MultiColumnSortState[] = [];

const VALID_ORDER_BY_FIELDS: readonly string[] = Object.values(
  InvoiceReportOrderByField
);
const isValidOrderByField = (
  value: string
): value is InvoiceReportOrderByField => VALID_ORDER_BY_FIELDS.includes(value);

// Helper function to parse sort orders from URL parameters
const parseSortOrdersFromParams = (
  sortFieldsParam: string | null,
  sortOrdersParam: string | null
): MultiColumnSortState[] => {
  if (!sortFieldsParam || !sortOrdersParam) return EMPTY_SORT_ORDERS;

  const fields = sortFieldsParam.split(',');
  const orders = sortOrdersParam.split(',');

  if (fields.length !== orders.length) return EMPTY_SORT_ORDERS;

  const result = fields
    .map((field, index) => {
      const order = orders[index];
      if (!field || !order) return null;
      if (!isValidOrderByField(field)) return null;
      const orderValue = order === 'asc' ? 'ascend' : 'descend';
      return {
        order: orderValue,
        columnKey: mapApiSortFieldToColumnKey(field),
      };
    })
    .filter((s): s is MultiColumnSortState => s !== null);

  return result.length > 0 ? result : EMPTY_SORT_ORDERS;
};

// Optimized search query hook with better state management and reduced re-renders
export const useInvoiceReportsHandleSearchQuery = (
  defaultValues: InvoiceReportsSearchFormData
) => {
  const [param] = useQueryParameter();
  const initialSearchQuery = useMemo(
    () => convertFormDataToApiQuery(defaultValues),
    [defaultValues]
  );

  const [searchQuery, setSearchQuery] =
    useState<InvoiceReportsSearchQuery>(initialSearchQuery);

  // Use ref to avoid searchQuery in callback dependencies
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  // Derive sortOrders from URL parameters using primitive values
  const sortFieldsParam = param.get('sort_fields');
  const sortOrdersParam = param.get('sort_orders');

  const sortOrders = useMemo(
    () => parseSortOrdersFromParams(sortFieldsParam, sortOrdersParam),
    [sortFieldsParam, sortOrdersParam]
  );

  const updateSearchParams = useUpdateURLSearchParams();

  // Stable search handler using ref
  const onSearch = useCallback(
    (newQuery: InvoiceReportsSearchQuery, refetchTable: () => void) => {
      const currentSearchQuery = searchQueryRef.current;
      const id = param.get('id') || undefined;
      const page = newQuery?.page;

      // Handle page-only updates
      if ('page' in newQuery && Object.keys(newQuery).length === 1) {
        const updatedQuery = {
          ...currentSearchQuery,
          page,
          sort_fields: currentSearchQuery.sort_fields,
          sort_orders: currentSearchQuery.sort_orders,
        };
        updateSearchParams(id ? { ...updatedQuery, id } : updatedQuery);
        setSearchQuery(updatedQuery);
        return;
      }

      // Handle full search with filters
      const searchPayload = convertFormDataToApiQuery(newQuery);
      const completeSearchQuery = {
        ...searchPayload,
        page: 1,
        sort_fields: newQuery.sort_fields,
        sort_orders: newQuery.sort_orders,
      };

      updateSearchParams(completeSearchQuery);

      if (isEqual(currentSearchQuery, completeSearchQuery)) {
        refetchTable();
      } else {
        setSearchQuery(completeSearchQuery);
      }
    },
    [param, updateSearchParams]
  );

  const handleSortChange = useCallback(
    (field: SortField, order: SortOrder) => {
      const currentSearchQuery = searchQueryRef.current;
      const transformedSortField = mapColumnKeyToApiSortField(field);

      const newSearchQuery: InvoiceReportsSearchQuery = {
        ...currentSearchQuery,
        sort_fields: order ? transformedSortField : undefined,
        sort_orders: order ? (order === 'ascend' ? 'asc' : 'desc') : undefined,
      };

      setSearchQuery(newSearchQuery);
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      updateSearchParams(newSearchQuery as Record<string, unknown>);
    },
    [updateSearchParams]
  );

  // Multi-column sort handler with stable reference
  const handleMultiColumnSort = useCallback(
    (sortStates: MultiColumnSortState[]) => {
      const currentSearchQuery = searchQueryRef.current;
      let newSortFields: string | undefined;
      let newSortOrders: string | undefined;

      if (sortStates.length > 0) {
        newSortFields = sortStates
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          .map((s) => mapColumnKeyToApiSortField(s.columnKey as SortField))
          .join(',');
        newSortOrders = sortStates
          .map((s) => (s.order === 'ascend' ? 'asc' : 'desc'))
          .join(',');
      }

      const newSearchQuery: InvoiceReportsSearchQuery = {
        ...currentSearchQuery,
        sort_fields: newSortFields,
        sort_orders: newSortOrders,
      };

      setSearchQuery(newSearchQuery);
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      updateSearchParams(newSearchQuery as Record<string, unknown>);
    },
    [updateSearchParams]
  );

  return {
    searchQuery,
    onSearch,
    handleSortChange,
    handleMultiColumnSort,
    sortOrders,
  };
};

export { useImportDrawer } from './useImportDrawer';
export { useCheckProxyApplicantPermission } from './useCheckProxyApplicantPermission';
